import React, { useState } from 'react';
import { DollarSign, Download, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализации затрат блокчейн-проекта
 */
const CostBreakdown = ({ costDetails, results }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  
  if (!costDetails || costDetails.length === 0 || !results) {
    return null;
  }

  // Получение уникальных категорий из данных
  const categories = ['all', ...new Set(costDetails.map(item => item.category))];
  
  // Фильтрация данных по активной категории
  const filteredCostDetails = activeCategory === 'all' 
    ? costDetails 
    : costDetails.filter(item => item.category === activeCategory);

  // Сортировка данных по убыванию суммы
  const sortedCostDetails = [...filteredCostDetails].sort((a, b) => b.amount - a.amount);

  // Форматирование суммы
  const formatAmount = (amount) => `$${formatNumber(amount, 0)}`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-indigo-500" />
          Детализация затрат
        </h3>
      </div>

      {/* Вкладки категорий */}
      <div className="flex overflow-x-auto space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4 pb-1">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`py-2 px-4 text-sm font-medium whitespace-nowrap ${
              activeCategory === category
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {category === 'all' 
              ? 'Все категории' 
              : category === 'Frontend' ? 'Фронтенд'
              : category === 'Backend' ? 'Бэкенд'
              : category === 'Смарт-контракты' ? 'Смарт-контракты'
              : category === 'Безопасность' ? 'Безопасность'
              : category === 'Тестирование' ? 'Тестирование'
              : category === 'Деплоймент' ? 'Деплоймент'
              : category === 'Поддержка' ? 'Поддержка'
              : category === 'Локация' ? 'Локация команды'
              : category === 'Сроки' ? 'Сроки проекта'
              : category === 'Доп. функции' ? 'Доп. функции'
              : category}
          </button>
        ))}
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Детальная разбивка стоимости проекта по категориям. Фактическая стоимость может варьироваться в зависимости от конкретных требований и изменений в ходе проекта.</p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[500px] cost-breakdown-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[600px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-4 py-2.5 font-semibold">Категория</th>
              <th scope="col" className="px-4 py-2.5 font-semibold">Статья расходов</th>
              <th scope="col" className="px-4 py-2.5 font-semibold text-right">Сумма</th>
              <th scope="col" className="px-4 py-2.5 font-semibold text-right">% от общего</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedCostDetails.map((item, index) => {
              const percentage = (item.amount / results.totalCost) * 100;
              return (
                <tr
                  key={index}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
                >
                  <td className="px-4 py-2.5 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {item.category}
                  </td>
                  <td className="px-4 py-2.5">
                    {item.name}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-right font-medium">
                    {formatAmount(item.amount)}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-right">
                    {percentage.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Итоговая строка */}
          <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold sticky bottom-0">
            <tr>
              <td className="px-4 py-3" colSpan="2">
                {activeCategory === 'all' ? 'Общая стоимость проекта' : `Итого по категории "${activeCategory}"`}
              </td>
              <td className="px-4 py-3 text-right">
                {formatAmount(
                  activeCategory === 'all'
                    ? results.totalCost
                    : costDetails
                        .filter(item => item.category === activeCategory)
                        .reduce((sum, item) => sum + item.amount, 0)
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {activeCategory === 'all'
                  ? '100%'
                  : ((costDetails
                      .filter(item => item.category === activeCategory)
                      .reduce((sum, item) => sum + item.amount, 0) / results.totalCost) * 100).toFixed(1) + '%'
                }
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Сравнение с аналогичными проектами */}
      <div className="mt-5 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Сравнение с рыночными ценами</h4>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-600 dark:text-gray-300">Нижняя граница рынка</span>
              <span className="font-medium text-red-600 dark:text-red-400">
                {formatAmount(Math.round(results.totalCost * 0.7))}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
              <div className="bg-red-500 h-1.5 rounded-full" style={{ width: '100%' }}></div>
            </div>
          </div>
          
          <div className="px-4 py-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg text-center">
            <span className="block text-xs text-indigo-600 dark:text-indigo-400">Ваш проект</span>
            <span className="font-bold text-indigo-700 dark:text-indigo-300">
              {formatAmount(Math.round(results.totalCost))}
            </span>
          </div>
          
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-600 dark:text-gray-300">Верхняя граница рынка</span>
              <span className="font-medium text-green-600 dark:text-green-400">
                {formatAmount(Math.round(results.totalCost * 1.5))}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
              <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          *Данная оценка основана на среднерыночных ценах для аналогичных проектов в {results.teamLocation === 'Восточная Европа' ? 'восточноевропейском' : results.teamLocation === 'Западная Европа/США' ? 'западном' : results.teamLocation === 'Азия' ? 'азиатском' : 'глобальном'} регионе.
        </p>
      </div>
    </div>
  );
};

export default CostBreakdown;